import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize, LocateFixed } from "lucide-react";

const PAD = 48;

export const ZoomCanvas = ({ children, testid = "tree-canvas", focusId }) => {
  const box = useRef(null);
  const inner = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(null);
  const btn = "w-9 h-9 grid place-items-center rounded-full bg-white border border-[#E5DEC9] hover:border-[#1E3A2B] transition-colors";

  useLayoutEffect(() => {
    const el = inner.current;
    const ro = new ResizeObserver(() => setNat({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = nat.w && box.current ? Math.max(0.3, Math.min(1, (box.current.clientWidth - PAD) / nat.w)) : 1;
  const z = zoom ?? fit;

  const center = useCallback(() => {
    const el = focusId && box.current?.querySelector(`[data-testid="person-card-${focusId}"]`);
    if (!el) return;
    const b = box.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    box.current.scrollTo({
      left: box.current.scrollLeft + r.left - b.left - b.width / 2 + r.width / 2,
      top: box.current.scrollTop + r.top - b.top - b.height / 2 + r.height / 2,
      behavior: "smooth",
    });
  }, [focusId]);

  useEffect(() => { const id = setTimeout(center, 400); return () => clearTimeout(id); }, [center, nat.w]);

  const step = (d) => setZoom(Math.max(0.3, Math.min(1.6, +(z + d).toFixed(2))));

  return (
    <div className="relative rounded-3xl border border-[#E5DEC9] bg-[#F4EDE2]/50" data-testid={testid}>
      <div className="absolute right-4 top-4 z-10 flex gap-2">
        {focusId && <button className={`${btn} w-auto px-3 gap-1.5 flex text-xs`} onClick={center} data-testid={`${testid}-center-me`} aria-label="Center on me"><LocateFixed className="w-4 h-4" />Me</button>}
        <button className={btn} onClick={() => step(-0.1)} data-testid={`${testid}-zoom-out`} aria-label="Zoom out"><ZoomOut className="w-4 h-4" /></button>
        <button className={btn} onClick={() => setZoom(null)} data-testid={`${testid}-zoom-reset`} aria-label="Fit whole tree"><Maximize className="w-4 h-4" /></button>
        <button className={btn} onClick={() => step(0.1)} data-testid={`${testid}-zoom-in`} aria-label="Zoom in"><ZoomIn className="w-4 h-4" /></button>
      </div>
      <div ref={box} className="overflow-auto min-h-[420px] max-h-[78vh] px-6 pb-8 pt-16">
        <div style={{ width: nat.w * z || "auto", height: nat.h * z || "auto" }} className="mx-auto relative">
          <div ref={inner} style={{ transform: `scale(${z})`, transformOrigin: "top left", transition: "transform .2s ease" }} className="w-max absolute left-0 top-0">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
